import { Component, OnInit, AfterViewInit } from '@angular/core';
import { Router } from '@angular/router';
import { AuthService } from 'src/app/auth/auth.service';
import { FeedbackType } from 'src/app/general-components/feedback/feedback-type';
import { FeedbackSettings } from 'src/app/general-components/feedback/feedback-settings';
import { Profile } from 'src/app/general-components/profile/profile';
import {
  KrampPriceExportState,
  KrampPriceExportStateForUpdate,
  KrampPriceExportStateValues,
} from '../kramp-price-export-state-types';
import { PricingExportService } from '../pricing-export.service';

@Component({
  selector: 'app-kramp-price-export-flow-control',
  templateUrl: './kramp-price-export-flow-control.component.html',
  styleUrls: ['./kramp-price-export-flow-control.component.scss'],
})
export class KrampPriceExportFlowControlComponent implements OnInit, AfterViewInit {
  profile!: Profile;

  currentExportState: KrampPriceExportState | null = null;
  selectedExportState: KrampPriceExportStateValues | '' = '';
  exportComment: string = '';
  readonly krampPriceExportStateValues = KrampPriceExportStateValues;
  readonly availableExportStates: KrampPriceExportStateValues[] = [
    KrampPriceExportStateValues.MS_IDLE,
    KrampPriceExportStateValues.MS_DATA_READY,
    KrampPriceExportStateValues.MS_RUNNING_EXPORT,
  ];
  isExportStateLoading: boolean = false;
  isExportStateUpdating: boolean = false;

  feedbackSettings: FeedbackSettings = {
    identifier: '',
    show: false,
    type: FeedbackType.error,
    message: 'Er is iets misgegaan sorry',
    closeButton: false,
  };
  hasProductFactoryAccess: boolean = false;
  cognitoGroups: string[] = [];

  private readonly productFactoryGroupName = 'AWS-PF';

  constructor(
    private service: PricingExportService,
    private authService: AuthService,
    private router: Router
  ) {}

  get isLoading(): boolean {
    return this.isExportStateLoading;
  }

  get showUnauthorizedWarning(): boolean {
    return !this.hasProductFactoryAccess;
  }

  ngOnInit(): void {
    this.cognitoGroups = this.authService.getCognitoGroups();
    this.hasProductFactoryAccess = this.cognitoGroups.includes(this.productFactoryGroupName);

    if (this.hasProductFactoryAccess) {
      this.loadExportState();
    }
  }

  ngAfterViewInit(): void {
    if (this.hasProductFactoryAccess) {
      this.profile = this.authService.getUserInfo();
    }
  }

  updateFeedback(
    type: FeedbackType,
    message: string,
    closeButton: boolean = true,
    buttonText: string | undefined = undefined
  ): void {
    this.feedbackSettings.identifier = '';
    this.feedbackSettings.show = true;
    this.feedbackSettings.closeButton = closeButton;
    this.feedbackSettings.buttonText = buttonText;
    this.feedbackSettings.type = type;
    this.feedbackSettings.message = message;
  }

  loadExportState(): void {
    this.isExportStateLoading = true;
    this.service.getKrampPriceExportState().subscribe({
      next: (state: KrampPriceExportState) => {
        this.currentExportState = state;
        this.selectedExportState = state.state;
        this.exportComment = '';
        this.isExportStateLoading = false;
      },
      error: (error: Error) => {
        this.updateFeedback(
          FeedbackType.error,
          'Fout opgetreden bij het ophalen van de exportstaat',
          false,
          'Terug naar hoofdmenu'
        );
        this.isExportStateLoading = false;
        console.error(error);
      },
    });
  }

  updateExportState(): void {
    if (!this.selectedExportState) {
      this.updateFeedback(FeedbackType.error, 'Selecteer een exportstaat om bij te werken');
      return;
    }

    this.isExportStateUpdating = true;

    const updateData: KrampPriceExportStateForUpdate = {
      state: this.selectedExportState,
      comment: this.exportComment || '',
      updatedByHint: this.profile.fullName,
    };

    this.service.updateKrampPriceExportState(updateData).subscribe({
      next: (updatedState: KrampPriceExportState) => {
        this.currentExportState = updatedState;
        this.selectedExportState = updatedState.state;
        this.exportComment = '';
        this.updateFeedback(FeedbackType.success, 'Exportstaat succesvol bijgewerkt');
        this.isExportStateUpdating = false;
      },
      error: (error: Error) => {
        this.updateFeedback(
          FeedbackType.error,
          'Fout opgetreden bij het bijwerken van de exportstaat',
          false,
          'Terug naar hoofdmenu'
        );
        this.isExportStateUpdating = false;
        console.error(error);
      },
    });
  }

  canUpdateExportState(): boolean {
    if (this.currentExportState == null) {
      return false;
    }

    return (
      this.selectedExportState !== '' &&
      this.selectedExportState !== this.currentExportState.state &&
      !this.isExportStateUpdating
    );
  }

  getExportStateDisplayName(state: KrampPriceExportStateValues): string {
    switch (state) {
      case KrampPriceExportStateValues.MS_IDLE:
        return 'Inactief';
      case KrampPriceExportStateValues.MS_DATA_READY:
        return 'Data gereed';
      case KrampPriceExportStateValues.MS_RUNNING_EXPORT:
        return 'Export wordt uitgevoerd';
      default:
        return state;
    }
  }

  getExportStateBadgeClass(state: KrampPriceExportStateValues): string {
    switch (state) {
      case KrampPriceExportStateValues.MS_IDLE:
        return 'badge bg-secondary';
      case KrampPriceExportStateValues.MS_DATA_READY:
        return 'badge bg-warning text-dark';
      case KrampPriceExportStateValues.MS_RUNNING_EXPORT:
        return 'badge bg-primary';
      default:
        return 'badge bg-secondary';
    }
  }

  returnHome(): void {
    void this.router.navigate(['home']);
  }
}
